import { useMapped } from "@omni/utils/context/mapped.context";
import { isDefined } from "@omni/utils/helper.utils";
import {
    createContext,
    Dispatch,
    ReactNode,
    RefObject,
    SetStateAction,
    use
} from "react";

import { useDepth } from "@/context/depth.context";
import { FlyoutCategory } from "@/redux/modules/value/flyoutCategories/flyoutCategories.types";

type SidebarContextValue = {
    open: boolean;
    accountLinks: Array<{
        label: string;
        link: string;
    }>;
    profileLink: string;
    innerHtml?: string;
    activeCategoryIndexes: Array<number>;
    categories: FlyoutCategory[];
    assortimentOpen: boolean;
    subCategoriesActive: boolean;
    contentElementRef?: RefObject<HTMLDivElement | null>;
    setAssortimentOpen: Dispatch<SetStateAction<boolean>>;
    setActiveCatetoryIndexes: Dispatch<SetStateAction<Array<number>>>;
    onClose: () => void;
};

type SidebarContextProviderProps = {
    open: boolean;
    accountLinks: Array<{
        label: string;
        link: string;
    }>;
    profileLink: string;
    innerHtml?: string;
    rootCategory: FlyoutCategory;
    activeCategoryIndexes: Array<number>;
    assortimentOpen: boolean;
    subCategoriesActive: boolean;
    contentElementRef?: RefObject<HTMLDivElement | null>;
    setAssortimentOpen: Dispatch<SetStateAction<boolean>>;
    setActiveCatetoryIndexes: Dispatch<SetStateAction<Array<number>>>;
    onClose: () => void;
    children: ReactNode;
};

export const SidebarContext = createContext<SidebarContextValue | undefined>(
    undefined
);
SidebarContext.displayName = "SidebarContext";

export const SidebarContextProvider = ({
    open,
    accountLinks,
    profileLink,
    innerHtml,
    rootCategory,
    activeCategoryIndexes,
    assortimentOpen,
    subCategoriesActive,
    setAssortimentOpen,
    contentElementRef,
    setActiveCatetoryIndexes,
    onClose,
    children
}: SidebarContextProviderProps) => {
    const categories = activeCategoryIndexes.reduce(
        (acc, curr) => {
            const subCategory = acc.at(-1)?.subCategories?.[curr];
            if (!subCategory) return acc;

            acc.push(subCategory);

            return acc;
        },
        [rootCategory]
    );

    return (
        <SidebarContext
            value={{
                open: open,
                accountLinks: accountLinks,
                profileLink: profileLink,
                innerHtml: innerHtml,
                activeCategoryIndexes: activeCategoryIndexes,
                categories: categories,
                assortimentOpen: assortimentOpen,
                subCategoriesActive: subCategoriesActive,
                contentElementRef: contentElementRef,
                setAssortimentOpen: setAssortimentOpen,
                setActiveCatetoryIndexes: setActiveCatetoryIndexes,
                onClose: onClose
            }}
        >
            {children}
        </SidebarContext>
    );
};

export const useSidebarContext = () => {
    const context = use<SidebarContextValue | undefined>(SidebarContext);

    if (!isDefined(context)) {
        throw new Error(`${SidebarContext.displayName} is undefined.`);
    }

    return context;
};

export const useCategory = () => {
    const { categories } = useSidebarContext();
    const { index } = useMapped(0);
    const { depth } = useDepth();

    return { category: categories?.at(index + depth) };
};

export const useSubCategory = () => {
    const { categories } = useSidebarContext();
    const { index } = useMapped();
    const { depth } = useDepth();

    return { subCategory: categories?.at(depth)?.subCategories?.at(index) };
};
